<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          General Properties
        </template>
        <template #actions v-if="hasModule('SYS:SYS:CONFIGMGR:EDIT')">
          <ul>
            <li class="p-narrow no-border">
              <div class="list-content">
                <a role="button"
                  @keypress.enter.prevent="editPanel()"
                  @click="editPanel()"
                  :class="{ disabled: editOtherPanels }"
                  v-if="!editCurrentPanel"
                  :tabindex="editOtherPanels ? -1 : 0"
                >
                  <font-awesome-icon :icon="['far', 'edit']" size="lg" title="Edit" />
                </a>
              </div>
            </li>
          </ul>
        </template>
      </EsHeader>
    </div>
    <div class="content">
      <es-form style="padding: 1.15rem 0rem">
        <template #es-form-header>
          <div class="header-container">
            <div class="header-elements">
              <font-awesome-icon :icon="['fas', 'cog']" size="lg"/>
            </div>
            <strong class="title is-6">
              Display Properties
            </strong>
          </div>
        </template>
        <template #es-form>
          <table>
            <tbody>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Branding: Application Name
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel, 'is-danger' : errAppName }"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.GENERAL.APP_NAME"
                      @keyup.prevent="validateInputField(form.GENERAL.APP_NAME)"
                      :aria-label="form.GENERAL.APP_NAME || 'Branding: Application Name'"
                    >
                    <span v-if="errAppName" class="help is-danger">
                      {{ msgAppName }}
                    </span>
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Branding: Header
                  <span v-if="editCurrentPanel">
                    (optional)
                  </span>
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel, 'is-danger' : errHeader }"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.GENERAL.HEADER"
                      :aria-label="form.GENERAL.HEADER || 'Branding: Header'"
                    >
                    <span v-if="errHeader" class="help is-danger">
                      {{ msgHeader }}
                    </span>
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Branding: Footer
                  <span v-if="editCurrentPanel">
                    (optional)
                  </span>
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel, 'is-danger' : errFooter }"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.GENERAL.FOOTER"
                      :aria-label="form.GENERAL.FOOTER || 'Branding: Footer'"
                    >
                    <span v-if="errFooter" class="help is-danger">
                      {{ msgFooter }}
                    </span>
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  State ID Display Format
                  <span v-if="editCurrentPanel">
                    (optional)
                  </span>
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel, 'is-danger' : errStateId }"
                      @keypress="checkStateIdInput($event)"
                      @keyup.prevent="validateInputField(form.GENERAL.STATE_ID_DISP_FORMAT)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.GENERAL.STATE_ID_DISP_FORMAT"
                      :aria-label="form.GENERAL.STATE_ID_DISP_FORMAT || 'State ID Display Format'"
                    >
                  </div>
                  <span v-if="errStateId" class="help is-danger">
                    {{ msgStateId }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </es-form>
      <es-form style="padding: 0rem 0rem 1.15rem 0rem">
        <template #es-form-header>
          <div class="header-container">
            <div class="header-elements">
              <font-awesome-icon :icon="['fas', 'cog']" size="lg"/>
            </div>
            <strong class="title is-6">
              Default Properties
            </strong>
          </div>
        </template>
        <template #es-form>
          <table>
            <tbody>
              <tr>
                <td></td>
                <th class="property has-text-grey top-row table-header-normal">
                  Session Timeout (Minutes)
                </th>
                <td class="top-row">
                  <div class="control">
                    <input
                      style="width: 25%"
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel, 'is-danger' : errSessionTimeout }"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.SESSION.TIMEOUT"
                      :aria-label="form.SESSION.TIMEOUT || 'Session Timeout (Minutes)'"
                    >
                    <span v-if="errSessionTimeout" class="help is-danger">
                      {{ msgSessionTimeout }}
                    </span>
                  </div>
                </td>
              </tr>
               <tr>
                  <td></td>
                  <th class="property has-text-grey top-row table-header-normal">
                    Download Masking
                  </th>
                  <td class="top-row">
                    <div class="control">
                      <div v-if="editCurrentPanel">
                        <button
                            class="button button--form is-pulled-right"
                            v-for="(option, index) in booleanOptions "
                            @click= "form.DOWNLOAD.ENABLE_MASKING_ON_EXTRACT = option.code"
                            :class="{
                              'is-active': form.DOWNLOAD.ENABLE_MASKING_ON_EXTRACT === option.code
                            }"
                            :key="index"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else class="is-pulled-right">
                        {{ form.DOWNLOAD.ENABLE_MASKING_ON_EXTRACT === true ? 'Yes' : 'No' }}
                      </div>
                   </div>
                 </td>
              </tr>
            </tbody>
          </table>
        </template>
      </es-form>
    </div>
    <div
      v-if="editCurrentPanel"
      class="is-flex flex-justify--end flex-align--center"
      style="margin-right: 5.15rem; padding-bottom: 2rem"
    >
      <button
        class="button button-link has-underline"
        @click.prevent="cancelPanel()"
        style="margin-right: 1rem"
      >
        Cancel
      </button>
      <button
        class="button is-primary"
        @click.prevent="updatePanel()"
      >
        Update
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faEdit,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faCog,
} from '@fortawesome/pro-solid-svg-icons';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faCog,
  faEdit,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsForm,
    EsHeader,
  },
  props: {
    editOtherPanels: {
      type: Boolean,
    },
  },
  data() {
    return this.initialData();
  },
  created() {
    this.initialize(this.properties);
    this.booleanOptions = [
      {
        code: true,
        description: 'Yes',
      },
      {
        code: false,
        description: 'No',
      },
    ];
  },
  mounted() {
    // eslint-disable-next-line no-unused-expressions
    this.form;
    this.updateCache();
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
  },
  methods: {
    ...mapActions({
      initProperties: 'propertiesModule/init',
    }),
    cancelPanel() {
      this.undoForm();
      this.resetFormErrors();
      this.hideNotification();
      this.editCurrentPanel = false;
      this.$emit('allowEditOtherPanels');
    },
    checkForFormChanges() {
      return this.compareObjects(this.cache.GENERAL, this.form.GENERAL)
        || this.compareObjects(this.cache.SESSION, this.form.SESSION)
        || this.compareObjects(this.cache.DOWNLOAD, this.form.DOWNLOAD);
    },
    checkStateIdInput(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except 9 and (-)
      if (charCode !== 45 && charCode !== 57) {
        evt.preventDefault();
      }
    },
    compareObjects(o, n) {
      return JSON.stringify(o) !== JSON.stringify(n);
    },
    editPanel() {
      this.editCurrentPanel = true;
      this.$emit('allowEditOtherPanels');
    },
    groupBySection(data, type) {
      return Object.entries(data)
        .filter(([key]) => key.includes(type))
        .map(([key, val]) => ({ [key.replace(type, '')]: val }));
    },
    initialData() {
      return {
        editCurrentPanel: false,

        cache: {},
        form: {},
        downloadMasking: null,

        errAppName: false,
        errFooter: false,
        errHeader: false,
        errSessionTimeout: false,
        errStateId: false,
        msgAppName: null,
        msgFooter: null,
        msgHeader: null,
        msgSessionTimeout: null,
        msgStateId: null,
      };
    },
    initialize(data) {
      const general = this.prepForDisplay('form', this.groupBySection(data, 'GENERAL-'));
      const session = this.prepForDisplay('form', this.groupBySection(data, 'SESSION-'));
      const download = this.prepForDisplay('form', this.groupBySection(data, 'DOWNLOAD-'));
      download.ENABLE_MASKING_ON_EXTRACT = JSON.parse(download.ENABLE_MASKING_ON_EXTRACT);
      this.form.GENERAL = general;
      this.form.SESSION = session;
      this.downloadMasking = download;
      this.form.DOWNLOAD = this.downloadMasking;
    },
    prepForDisplay(type, arrFromDb) {
      const o = {};
      arrFromDb.map((item) => {
        const key = Object.keys(item)[0];
        const value = item[key];
        o[key] = value;
        return 0;
      });
      return o;
    },
    prepForRequest() {
      const payload = [];
      let cached = null;
      let obj = null;
      obj = this.form.GENERAL;
      cached = this.cache.GENERAL;
      this.pushToPayLoad('GENERAL', payload, obj, cached);
      obj = this.form.SESSION;
      cached = this.cache.SESSION;
      this.pushToPayLoad('SESSION', payload, obj, cached);
      obj = this.form.DOWNLOAD;
      cached = this.cache.DOWNLOAD;
      this.pushToPayLoad('DOWNLOAD', payload, obj, cached);
      return payload;
    },
    pushToPayLoad(category, payload, obj, cached) {
      Object.entries(obj).forEach(([property, value]) => {
        if (value !== cached[property]) {
          payload.push({ category, property, value });
        }
      });
    },
    resetFormErrors() {
      this.errAppName = false;
      this.errFooter = false;
      this.errHeader = false;
      this.errSessionTimeout = false;
      this.errStateId = false;
      this.msgAppName = null;
      this.msgFooter = null;
      this.msgHeader = null;
      this.msgSessionTimeout = null;
      this.msgStateId = null;
    },
    showFormErrors(errors) {
      errors.forEach((e) => {
        if (e.property === 'STATE_ID_DISP_FORMAT') {
          this.errStateId = true;
          this.msgStateId = e.error;
        }
        if (e.property === 'TIMEOUT') {
          this.errSessionTimeout = true;
          this.msgSessionTimeout = e.error;
        }
        if (e.property === 'APP_NAME') {
          this.errAppName = true;
          this.msgAppName = e.error;
        }
        if (e.property === 'HEADER') {
          this.errHeader = true;
          this.msgHeader = e.error;
        }
        if (e.property === 'FOOTER') {
          this.errFooter = true;
          this.msgFooter = e.error;
        }
      });
    },
    undoForm() {
      this.form = JSON.parse(JSON.stringify(this.cache));
    },
    updateCache() {
      this.cache = JSON.parse(JSON.stringify(this.form));
    },
    async updatePanel() {
      this.$emit('allowEditOtherPanels');
      const changes = this.checkForFormChanges();
      if (!changes) {
        this.editCurrentPanel = false;
        this.hideNotification();
        return;
      }
      if (this.checkFieldValidations()) return;
      try {
        await axios({
          method: 'put',
          url: '/properties',
          data: this.prepForRequest(),
        });
        this.updateCache();
        await this.initProperties();
        this.showNotification('success', ['The General Properties have been updated successfully.']);
        this.editCurrentPanel = false;
      } catch (error) {
        if (error.response.status === 400) {
          this.$emit('allowEditOtherPanels');
          this.showFormErrors(error.response.data);
          this.showNotification('danger', ['There was an issue. Please try again or contact your administrator.']);
        } else {
          this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
        }
      }
    },
    validateInputField(userInput) {
      if (userInput === this.form.GENERAL.APP_NAME) {
        if (!this.form.GENERAL.APP_NAME) {
          this.errAppName = true;
          this.msgAppName = 'Branding: Application Name is required';
        } else if (this.form.GENERAL.APP_NAME && this.form.GENERAL.APP_NAME.length > 150) {
          this.errAppName = true;
          this.msgAppName = 'Branding: Application Name allows a maximum of 150 characters.';
        } else {
          this.errAppName = false;
          this.msgAppName = '';
        }
      }
      if (userInput === this.form.GENERAL.STATE_ID_DISP_FORMAT) {
        const inputVal = this.form.GENERAL.STATE_ID_DISP_FORMAT;
        if (inputVal && inputVal.match(/[9]/g).length > 20) {
          this.errStateId = true;
          this.msgStateId = 'State ID Display Format exceeds character limit (20).';
        } else {
          this.errStateId = false;
          this.msgStateId = '';
        }
      }
    },
    validStateIdFormat(userInput) {
      const regexSSN = (/^[^9]*(?:9[^9]*){20}$/gm);
      if (userInput) {
        if (regexSSN.test(userInput)) {
          this.errStateId = false;
          this.msgStateId = null;
        } else {
          this.errStateId = true;
          this.msgStateId = 'State ID should be of 10 digits.';
        }
      } else {
        this.errStateId = false;
        this.msgStateId = null;
      }
    },
    checkFieldValidations() {
      this.validateInputField(this.form.GENERAL.STATE_ID_DISP_FORMAT);
      const err = this.errStateId;
      if (err) {
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
      }
      return err;
    },
  },
};
</script>

<style lang="sass" scoped>
  table td
    vertical-align: middle
    &:first-child
      border: 0
      width: 30px
  table th
    vertical-align: middle
  a.disabled
    color: #999
    cursor: default
    text-decoration: none
    pointer-events: none
    &:hover
      text-decoration: none
  .property
    padding-left: 0
    font-size: 12px
    width: 35%
  .top-row
    padding-top: 1rem
  .no-border
    border: 0
  .header-elements
    margin: 0px 7px !important
</style>
